/**
 * Appeal Preparation Checklist Modal - Phase 1.4
 *
 * Pre-submission checklist to ensure users have completed all necessary
 * steps before submitting their appeal to the council portal.
 */

import { useState } from 'react';
import { CheckCircle2, Circle, AlertTriangle, Send } from 'lucide-react';
import Modal from './Modal';

interface AppealChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSubmit: () => void;
  ticketInfo: {
    pcnNumber: string;
    appealDeadline?: Date;
    hasEvidence?: boolean;
  };
}

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'review_text',
    label: 'I have reviewed my appeal text for accuracy and completeness',
    required: true
  },
  {
    id: 'verify_details',
    label: 'I have verified all ticket details (PCN number, VRM, dates, location)',
    required: true
  },
  {
    id: 'evidence_ready',
    label: 'I have prepared all supporting evidence (photos, documents)',
    required: false
  },
  {
    id: 'deadline_aware',
    label: 'I understand the appeal deadline and will submit before it expires',
    required: true
  },
  {
    id: 'portal_ready',
    label: 'I am ready to submit this appeal via the council portal',
    required: true
  }
];

export default function AppealChecklistModal({
  isOpen,
  onClose,
  onConfirmSubmit,
  ticketInfo
}: AppealChecklistModalProps) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const handleToggle = (itemId: string) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const allRequiredChecked = CHECKLIST_ITEMS
    .filter(item => item.required)
    .every(item => checkedItems.has(item.id));

  const handleSubmit = () => {
    if (allRequiredChecked) {
      onConfirmSubmit();
      setCheckedItems(new Set()); // Reset for next time
    }
  };

  const handleClose = () => {
    setCheckedItems(new Set()); // Reset on close
    onClose();
  };

  // Calculate urgency for deadline warning
  const getDeadlineUrgency = () => {
    if (!ticketInfo.appealDeadline) return null;

    const now = new Date();
    const daysRemaining = Math.ceil(
      (ticketInfo.appealDeadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysRemaining <= 3) return 'critical';
    if (daysRemaining <= 7) return 'warning';
    return 'normal';
  };

  const deadlineUrgency = getDeadlineUrgency();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Appeal Preparation Checklist"
      maxWidth="max-w-lg"
    >
      <div className="p-6 space-y-6">
        {/* Deadline Warning */}
        {ticketInfo.appealDeadline && deadlineUrgency && (
          <div
            className={`rounded-lg p-4 flex items-start gap-3 ${
              deadlineUrgency === 'critical'
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50'
                : deadlineUrgency === 'warning'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50'
                : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/50'
            }`}
          >
            <AlertTriangle
              size={20}
              className={
                deadlineUrgency === 'critical'
                  ? 'text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5'
                  : deadlineUrgency === 'warning'
                  ? 'text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5'
                  : 'text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5'
              }
            />
            <div>
              <p
                className={`font-medium ${
                  deadlineUrgency === 'critical'
                    ? 'text-red-900 dark:text-red-300'
                    : deadlineUrgency === 'warning'
                    ? 'text-yellow-900 dark:text-yellow-300'
                    : 'text-blue-900 dark:text-blue-300'
                }`}
              >
                Appeal Deadline: {ticketInfo.appealDeadline.toLocaleDateString('en-GB')}
              </p>
              <p
                className={`text-sm mt-1 ${
                  deadlineUrgency === 'critical'
                    ? 'text-red-700 dark:text-red-400'
                    : deadlineUrgency === 'warning'
                    ? 'text-yellow-700 dark:text-yellow-400'
                    : 'text-blue-700 dark:text-blue-400'
                }`}
              >
                {Math.ceil(
                  (ticketInfo.appealDeadline.getTime() - new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                )}{' '}
                days remaining
              </p>
            </div>
          </div>
        )}

        {/* Introduction */}
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Before submitting your appeal for PCN <span className="font-mono font-medium text-slate-900 dark:text-white">{ticketInfo.pcnNumber}</span>,
            please confirm you have completed the following steps:
          </p>
        </div>

        {/* Checklist Items */}
        <div className="space-y-3">
          {CHECKLIST_ITEMS.map(item => (
            <label
              key={item.id}
              className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={checkedItems.has(item.id)}
                onChange={() => handleToggle(item.id)}
                className="sr-only"
              />
              <div className="flex-shrink-0 mt-0.5">
                {checkedItems.has(item.id) ? (
                  <CheckCircle2 size={20} className="text-green-600 dark:text-green-400" />
                ) : (
                  <Circle size={20} className="text-slate-300 dark:text-slate-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-900 dark:text-white">
                  {item.label}
                  {item.required && (
                    <span className="text-red-500 ml-1" title="Required">
                      *
                    </span>
                  )}
                </p>
              </div>
            </label>
          ))}
        </div>

        {/* Warning about manual submission */}
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            <strong className="text-slate-900 dark:text-white">Important:</strong> PCNGuard prepares
            your appeal materials. You are responsible for submitting via the council portal and
            meeting all deadlines.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!allRequiredChecked}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={!allRequiredChecked ? 'Please complete all required items' : undefined}
          >
            <Send size={16} />
            Confirm & Continue
          </button>
        </div>
      </div>
    </Modal>
  );
}
